import React, { useCallback, useMemo, useState } from 'react';
import styles from '../../styles.module.scss';
import { Venues } from '@weddingspot/ws-api-client';

import { TagIcon, MoreHorizontalIcon, XIcon, PlusIcon, ShoppingBagIcon, MinusIcon } from '@cvent/carina/components/Icon';
import Button from '@cvent/carina/components/Button';
import cn from 'classnames';
import { VenueProfileContext } from '../../context';
import AddToShoppingCartButtonComponent from '../../components/AddToShoppingCartButton';
import { CartItem, ShoppingCartContext, WSAppContext } from '@weddingspot/ws-core-components';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';

interface CTAMobileProps {
    priceVenueClickHandler: () => void;
    toUSD: (value: number, showCents: boolean) => string;
}

const CtaMobile = ({ priceVenueClickHandler, toUSD }: CTAMobileProps) => {
    const context = React.useContext(VenueProfileContext);
    const { getSRPViewFromSessionStorage } = React.useContext(WSAppContext);

    const wsVenueProfile = React.useMemo(() => context.wsVenueProfile, []) as Venues.VenueDetails;
    const { items, removeItem, addItem, setCartOpen } = React.useContext(ShoppingCartContext);
    const [isMenuOpen, setMenuOpen] = useState(false);

    const isInCart = useMemo(() => {
        return Boolean(items.find((item: CartItem) => item.id === wsVenueProfile.id));
    }, [wsVenueProfile, items]);
    const addVenue = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const value = {
            id: wsVenueProfile.id,
            name: wsVenueProfile.name,
            location: `${wsVenueProfile.city}, ${wsVenueProfile.state_abbr}`,
            imageUrl: wsVenueProfile.images[0] + '?unique=true',
            lastUpdated: new Date().getTime(),
            market: wsVenueProfile.market,
            isDestination: wsVenueProfile.is_destination,
            srpView: getSRPViewFromSessionStorage(wsVenueProfile.id),
            contractPackage: wsVenueProfile.contract_package,
        };
        addItem(value);
        logEvent(AnalyticsEvent.EVTNM_VENUE_ADDED_TO_CART, { source: 'Venue profile v3' });
    };
    const removeVenue = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        removeItem(wsVenueProfile.id);
        logEvent(AnalyticsEvent.EVTNM_CLICKED_REMOVE_FROM_LIST_CTA, { source: 'Venue profile v3' });
    };

    const openCart = useCallback(() => {
        setCartOpen(true);
        setMenuOpen(false);
    }, [setCartOpen]);

    return (
        <div className={cn(styles.CTAMobile, { [styles.CTAMobileOpen]: isMenuOpen })}>
            {isMenuOpen && (
                <div
                    className={styles.CTAMobileMenu}
                    onClick={() => {
                        setMenuOpen(false);
                    }}
                >
                    {!context.personalizedData?.estimate_completed && wsVenueProfile.is_priced && (
                        <button className={styles.MenuItem} onClick={isInCart ? removeVenue : addVenue}>
                            {isInCart ? 'Remove from list' : 'Select'}
                            <div className={cn(styles.MenuItemIcon, { [styles.transparent]: isInCart })}>
                                {isInCart ? <MinusIcon /> : <PlusIcon />}
                            </div>
                        </button>
                    )}
                    <button className={styles.MenuItem} onClick={openCart}>
                        View list
                        <div className={styles.MenuItemIcon}>
                            <ShoppingBagIcon />
                            <div className={styles.CartItemsCount}>{items.length}</div>
                        </div>
                    </button>
                </div>
            )}
            <div
                className={styles.CTAMobileBody}
                onClick={() => {
                    setMenuOpen(false);
                }}
            >
                <div className={styles.CTAMobileValues}>
                    {wsVenueProfile.discount && (
                        <div className={styles.CTApin}>
                            <TagIcon />
                        </div>
                    )}
                    {context.personalizedData?.spot_estimate && (
                        <div className={styles.CTAMobileSE} onClick={priceVenueClickHandler}>
                            Your Spot Estimate: {toUSD(context.personalizedData.spot_estimate, false)}
                        </div>
                    )}
                    {!context.personalizedData?.spot_estimate && wsVenueProfile.is_priced && !!wsVenueProfile.budget_range && (
                        <div className={styles.CTAStarting}>
                            Starting at <b>${Math.floor(wsVenueProfile.budget_range.min / 1000)}k </b>
                            for <b>{wsVenueProfile.budget_range.capacity}</b> Guests
                        </div>
                    )}
                </div>
                <div className={styles.CTAMobileActions}>
                    {!context.personalizedData?.estimate_completed && wsVenueProfile.is_priced && (
                        <Button text='Price venue' appearance={'filled'} isPill={true} onClick={priceVenueClickHandler} />
                    )}
                    <AddToShoppingCartButtonComponent />
                </div>
            </div>
        </div>
    );
};

export default CtaMobile;
